import {
  DATA_SOURCES_FOLDER_DEFINITIONS,
  type DataSourceSubfolderNestedDTO,
  DataSourcesFolderIds,
} from '@conversed/rest/src/contracts/data-sources-folders/data-sources-folders.schema';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Resolve folder/subfolder IDs and display names for a given folderId
 * from the Redux folder tree in a single pass.
 */
const resolveCurrentLocation = (
  folderId: string | undefined,
  folders: ReturnType<typeof useDataSources>['folders'],
  t: (key: any, opts?: any) => string,
) => {
  const empty = {
    currentFolderId: '',
    currentSubfolderId: '',
    currentFolderName: '',
    currentSubfolderName: '',
  };
  if (!folderId) return empty;

  for (const folder of folders) {
    const folderName = t(folder.name as any, { defaultValue: folder.name });
    if (folder._id?.toString() === folderId) {
      return {
        currentFolderId: folderId,
        currentSubfolderId: '',
        currentFolderName: folderName,
        currentSubfolderName: '',
      };
    }
    const sub = folder.subfolders?.find((s) => s._id?.toString() === folderId);
    if (sub) {
      return {
        currentFolderId: folder._id?.toString() ?? '',
        currentSubfolderId: folderId,
        currentFolderName: folderName,
        currentSubfolderName: sub.name,
      };
    }
  }
  return empty;
};

/**
 * Manages folder + subfolder selection state.
 * Pass `initialFolderId` when editing an existing file (e.g. Move modal).
 * Pass `defaultToCore=true` to pre-select the Core Knowledge folder (e.g. Upload sidebar).
 * Omit both when no default is needed.
 */
export const useFolderSelection = (
  initialFolderId?: string,
  defaultToCore?: boolean,
) => {
  const { folders } = useDataSources();
  const { t } = useTranslation();

  const {
    currentFolderId,
    currentSubfolderId,
    currentFolderName,
    currentSubfolderName,
  } = useMemo(
    () => resolveCurrentLocation(initialFolderId, folders, t),
    [initialFolderId, folders, t],
  );

  const [selectedFolderId, setSelectedFolderId] = useState(currentFolderId);
  const [selectedSubfolderId, setSelectedSubfolderId] =
    useState(currentSubfolderId);

  // When defaultToCore is set and no folder is selected yet, default to the Core Knowledge folder
  // once the folders list is available (it may be loaded asynchronously from Redux).
  useEffect(() => {
    if (!defaultToCore || selectedFolderId || !folders.length) return;

    const coreFolder = folders.find((f) =>
      f.name.startsWith(DataSourcesFolderIds.Core),
    );
    if (coreFolder?._id) {
      setSelectedFolderId(coreFolder._id.toString());
    }
  }, [defaultToCore, folders, selectedFolderId]);

  const resolvedFolderId: string | undefined =
    selectedSubfolderId || selectedFolderId || undefined;

  const isTargetTemporary = useMemo(() => {
    if (!resolvedFolderId) return false;
    const tempName =
      DATA_SOURCES_FOLDER_DEFINITIONS[DataSourcesFolderIds.Temporary].name;
    for (const folder of folders) {
      const isTemp = folder.name === tempName;
      if (folder._id?.toString() === resolvedFolderId) return isTemp;
      if (
        folder.subfolders?.some(
          (s: DataSourceSubfolderNestedDTO) =>
            s._id?.toString() === resolvedFolderId,
        )
      )
        return isTemp;
    }
    return false;
  }, [resolvedFolderId, folders]);

  const resetFolderSelection = () => {
    setSelectedFolderId('');
    setSelectedSubfolderId('');
  };

  return {
    folders,
    // Current (initial) location — useful for showing "current location" info in Move modal
    currentFolderId,
    currentSubfolderId,
    currentFolderName,
    currentSubfolderName,
    // Selected (target) location
    selectedFolderId,
    selectedSubfolderId,
    setSelectedFolderId,
    setSelectedSubfolderId,
    // Resolved ID to send to the API (subfolder takes priority over folder)
    resolvedFolderId,
    isTargetTemporary,
    resetFolderSelection,
  };
};
